import { getTokenDefinition, } from "@bracket/chainography";
import type { TokenIdentifier, } from "@bracket/types";
import { bracketRate, latestPriceEth, } from "@bracket/platform/cachables";
import { assetToUsd, } from "@bracket/platform/utils"
import ReactGA from "react-ga";


type BaseEventProps = {
	value: number; // USD amount
};

type BrktEventProps = BaseEventProps & {
	type: "buy" | "redeem_req" | "redeem_claim";
	tokenRedeemed?: string; // Only for redeem/redeem_claim
};

type VaultEventProps = BaseEventProps & {
	type: "invest" | "withdraw_req" | "withdraw_claim";
	symbol: string; // symbol_of_vault
	valueType: "shares" | "brkt" | "asset";
	denomination: TokenIdentifier;
};

/**
 * Calculate USD value from BRKT amount
 */
const calculateBrktUsdValue = async (
	brktAmount: number,
): Promise<number> => {

	// Get BRKT -> ETH rate
	const brktRate = await bracketRate();

	// Get ETH price in USD directly using latestPriceEth
	const ethPrice = await latestPriceEth();

	// Calculate USD value: amount (in BRKT) * BRKT->ETH rate * ETH->USD price
	return Number.parseFloat((brktAmount * brktRate * ethPrice).toFixed(2));

};


const calculateAssetUsdValue = async (
	assetAmount: number,
	token: TokenIdentifier,
): Promise<number> => {
	const tok = getTokenDefinition(token);
	const usd = await assetToUsd(BigInt(Math.floor(assetAmount * (10**tok.decimals))), tok);
	return usd;
}

export const calculateSharesToUsdValue = async (
	shares: number,
	token: TokenIdentifier,
) => {
	return Number.parseFloat(shares.toFixed(5));
}

/**
 * Track Escrow related events
 */
export const trackEscrowEvent = async ({ value }: BaseEventProps) => {

	try {

		if(globalThis.location.host !== "app.bracket.fi") {
			return;
		}

		let valueUSD = await calculateBrktUsdValue(value);
		valueUSD = valueUSD / 1e18;

		// Google Analytics
		ReactGA.event({
			category: "escrow_claim",
			action: "escrow_claim",
			label: "escrow_claim",
			value: valueUSD ,
		});

		// Cookie3
		if(window.cookie3) {
			window.cookie3.trackEvent({
				category: "escrow_claim",
				action: "escrow_claim",
				name: "escrow_claim",
				value: Math.round(valueUSD),
			});
		}
	} catch (error) {
		console.error("Error tracking escrow event:", error);
	}
};

/**
 * Track BRKT token related events
 */
export const trackBrktEvent = async ({
	type,
	value,
	tokenRedeemed,
}: BrktEventProps) => {

	try {

		if(globalThis.location.host !== "app.bracket.fi") {
			return;
		}

		const valueUSD = await calculateBrktUsdValue(value);

		// Google Analytics
		ReactGA.event({
			category: `brkt_${type}`,
			action: `brkt_${type}`,
			label: type === "buy" ? "brktETH" : tokenRedeemed,
			value: valueUSD,
		});

		// Cookie3
		if(window.cookie3) {
			window.cookie3.trackEvent({
				category: `brkt_${type}`,
				action: `brkt_${type}`,
				name: type === "buy" ? tokenRedeemed as string : `brkt_${type}`,
				value: valueUSD,
			});

		}
	}
	catch(err) {
		console.error("Error tracking BRKT event:", err);
	}
};

/**
 * Track Vault related events
 */

// call it in TransactionAction.tsx
export const trackVaultEvent = async ({
	type,
	value,
	symbol,
	valueType,
	denomination,
}: VaultEventProps) => {

	try {

		if(globalThis.location.host !== "app.bracket.fi") {
			return;
		}

		const tok = getTokenDefinition(denomination);

		if(valueType === "brkt") {
			console.warn("trackVaultEvent received a valuetype of brkt, this is being deprecated - use \"asset\" instead");
		}

		if(valueType === "shares") {
			console.warn("trackVaultEvent received a valuetype of shares, this is being deprecated - use \"asset\" instead");
		}

		const valueUSD = await (
			valueType === "shares"
				? calculateSharesToUsdValue(value, tok)
				: calculateAssetUsdValue(value, tok)
		);

		const category =
			type === "invest"
				? `vault_invest_${symbol}`
				: type === "withdraw_req"
					? `vault_withdraw_req_${symbol}`
					: `vault_withdraw_claim_${symbol}`;

		// Google Analytics
		ReactGA.event({
			category,
			action: category,
			label: category,
			value: valueUSD,
		});

		// Cookie3
		if(window.cookie3) {

			window.cookie3.trackEvent({
				category,
				action: category,
				name: category,
				value: valueUSD,
			});

		}
	}
	catch(err) {

		console.error("Error tracking vault event:", err);
	}

};

/*

Events spec:


ESCROW_CLAIM:  (when they first get the brktETH from escrow)

  GAEvent({
      category: "escrow_claim",
      action: "escrow_claim",
      label: 'brktETH',
      value: amountUSD,  // usd amount of brktETH claimed at 1.0 conversion to eth price.
  });

if (window.cookie3) {
  window.cookie3.trackEvent({
    category: "escrow",
    action: "claim",
    name: 'brktETH',
      value: amountUSD,  // usd amount of brktETH claimed at 1.0 conversion to eth price.
  });
}

BRKT_BUY:

  GAEvent({
      category: "brkt_buy",
      action: "brkt_buy",
      label: 'brktETH',
      value: amountUSD,  // USD amount of brktETH bought.
  });

if (window.cookie3) {
  window.cookie3.trackEvent({
    category: "brkt",
    action: "buy",
    name: 'brktETH',
    value: amountUSD,  // USD amount of brktETH bought.
  });
}


BRKT_REDEEM:

  GAEvent({
      category: "brkt_redeem",
      action: "brkt_redeem",
      label: tokenRedeemed, // if possible tell us what they redeemed.
      value: amountUSD,  // USD amount of brktETH redeemed.
  });

if (window.cookie3) {
  window.cookie3.trackEvent({
    category: "brkt",
    action: "redeem",
    name: tokenRedeemed, // if possible tell us what they redeemed.
    value: amountUSD,  // amount of brktETH bought.
  });
}

BRKT_REDEEM_CLAIM:

  GAEvent({
      category: "brkt_redeem_claim",
      action: "brkt_redeem_claim",
      label: tokenRedeemed, // if possible tell us what they redeemed.
      value: amountUSD,  // USD amount of what they receeved.
  });

if (window.cookie3) {
  window.cookie3.trackEvent({
    category: "brkt",
    action: "redeem_claim",
    name: tokenRedeemed, // if possible tell us what they redeemed.
    value: amountUSD,  // USD amount of what they receeved.
  });
}

VAULT_INVEST:  // deposit_request

  GAEvent({
      category: "vault_invest",
      action: "vault_invest",
      label: symbol_of_vault, 
      value: amountUSD,  // USD amount of brktETH sent.
  });

if (window.cookie3) {
  window.cookie3.trackEvent({
    category: "vault",
    action: "invest",
    name: symbol_of_vault, 
    value: amountUSD,  // USD amount of brktETH sent.
  });
}

VAULT_WITHDRAW_REQUEST:

  GAEvent({
      category: "vault_withdraw_req",
      action: "vault_withdraw_req",
      label: symbol_of_vault, 
      value: amountUSD,  // USD amount of shares at time of request.
  });

if (window.cookie3) {
  window.cookie3.trackEvent({
    category: "vault",
    action: "withdraw_req",
    name: symbol_of_vault, 
    value: amountUSD,  // USD amount of shares at time of request.
  });
}

VAULT_WITHDRAW_CLAIM:

  GAEvent({
      category: "vault_withdraw_claim",
      action: "vault_withdraw_claim",
      label: symbol_of_vault, 
      value: amountUSD,  // USD amount of brktETH claimed.
  });

if (window.cookie3) {
  window.cookie3.trackEvent({
    category: "vault",
    action: "withdraw_claim",
    name: symbol_of_vault, 
      value: amountUSD,  // USD amount of brktETH claimed.
  });
}

*/
